import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import NumberFormat from 'react-number-format';

import MandatoryField from 'modules/_shared/components/MandatoryField';
import ShareRanges from 'modules/_shared/components/ShareRanges';

import AvailableRangesView from 'modules/_shared/components/AvailableRangesView';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import HistoryMovementsView from 'modules/_shared/components/HistoryMovementsView';
import { AvailableRangesViewProvider } from 'modules/_shared/components/AvailableRangesView/context/AvailableRangesViewContext';
import { HistoryMovementsViewProvider } from 'modules/_shared/components/HistoryMovementsView/context/HistoryMovementsViewContext';

import { formatNumber } from 'utils/formats';
import { numberFormat } from 'constants/formats';
import operationTypes from 'constants/operationTypes';

import { useCapitalDecreaseModal } from '../context/CapitalDecreaseModalContext';

const CapitalDecreaseMovementsView: FC = () => {
  const { t } = useTranslate();

  const { data, actions } = useCapitalDecreaseModal();

  const additionalCondition =
    data.remainingShares < data.sharesToDecrease ||
    !data.selectedHoldingClass ||
    !data.selectedPartnerOption;

  const handleSearchPartnerByTerm = async (searchTerm: string) => {
    const options = await actions.handleSearchPartnerByTerm(searchTerm, true);
    return options;
  };

  return (
    <>
      <div>
        <label className="form-label mb-4">
          <span className="pb-2">{t('TotalParticipationsPendingDelete')}:</span>
          <span className="badge-primary rounded px-2 py-1 ml-2">
            {formatNumber(data.remainingShares >= 0 ? data.remainingShares : 0)}
          </span>
        </label>

        <div className="form-group">
          <label className="form-label" htmlFor="partner-select-input">
            {t('PartnerName')} <MandatoryField />
          </label>
          <div className="form-control-wrap">
            <CustomAsyncSelect
              id="partner-select-input"
              onChange={actions.handleChangePartner}
              onFetchData={handleSearchPartnerByTerm}
              value={data.selectedPartnerOption}
              placeholder={t('SearchPartner')}
              minimumInputLength={3}
              noDataLabelKey="NoPartnersHaveBeenFound"
              loadingLabelKey="LoadingPartners"
              errorLabelKey="ErrorLoadingPartners"
              defaultOptions={data.partnerOptions}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="class-select-input">
            {t('ClassOfParticipations')} <MandatoryField />
          </label>
          <div className="form-control-wrap">
            <CustomAsyncSelect
              id="class-select-input"
              value={data.selectedHoldingClassOption}
              onChange={actions.handleChangeHoldingClass}
              onFetchData={actions.handleFetchHoldingClassByTerm}
              placeholder={t('SearchClass')}
              minimumInputLength={1}
              noDataLabelKey="NoClassesHaveBeenFound"
              loadingLabelKey="LoadingClasses"
              errorLabelKey="ErrorLoadingClasses"
              defaultOptions={data.holdingClassOptions}
              isClearable={false}
            />
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <label className="form-label mb-0" htmlFor="pay-amount">
              {t('NºParticipationsThatElimi')}:
              <span className="badge-primary rounded px-2 py-1 ml-2">
                {formatNumber(data.sharesToDecrease || 0)}
              </span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <ShareRanges
            sharesRanges={data.sharesRanges}
            shareFrom={data.shareFrom}
            shareTo={data.shareTo}
            isValidRange={data.isValidRange}
            isValidatingRange={data.isValidatingRange}
            additionalConditions={additionalCondition}
            isTransferingAllShares={false}
            handleChangeShareFrom={actions.handleChangeShareFrom}
            handleChangeShareTo={actions.handleChangeShareTo}
            handleValidateRange={actions.handleValidateRange}
          />
        </div>

        <hr />
        <div className="form-group mb-0">
          <AvailableRangesViewProvider
            availableRanges={data.availableRanges}
            page={data.rangesPage}
            rowsPerPage={data.rangesRowsPerPage}
            totalAvailableRanges={data.totalAvailableRanges}
            isLoadingRanges={data.isLoadingAvailableRanges}
            handleChangePage={actions.handleChangeRangesPage}
            handleChangeRowsPerPage={actions.handleChangeRangesRowsPerPage}
            handleClickRange={actions.handleClickRange}
          >
            <AvailableRangesView />
          </AvailableRangesViewProvider>
        </div>
        <hr />

        <div className="form-group">
          <div className="custom-control custom-control-xs custom-checkbox mt-1">
            <input
              type="checkbox"
              id="checkbox-toggle"
              className="custom-control-input"
              checked={data.hasCustomSharePrice}
              onChange={actions.handleChangeHasCustomSharePrice}
            />
            <label
              className="custom-control-label mb-2"
              htmlFor="checkbox-toggle"
            >
              {t('ReportDecreaseValue')}
            </label>
          </div>

          {data.hasCustomSharePrice && (
            <div className="form-group">
              <label className="form-label" htmlFor="share-price">
                {t('SharePrice')} <MandatoryField />
              </label>

              <div className="form-control-wrap">
                <NumberFormat
                  id="share-price"
                  className="form-control form-control-lg"
                  value={data.sharePrice}
                  onChange={actions.handleChangeSharePrice}
                  thousandSeparator={numberFormat.thousandSeparator}
                  decimalSeparator={numberFormat.decimalSeparator}
                  allowNegative={false}
                  decimalScale={10}
                  required
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <HistoryMovementsViewProvider
          historyMovements={data.historyMovements}
          operationType={operationTypes.CAPITAL_DECREASE}
        >
          <HistoryMovementsView />
        </HistoryMovementsViewProvider>
      </div>
    </>
  );
};

export default CapitalDecreaseMovementsView;
